import React, { useState, useEffect, useRef } from "react";
import { TextField } from "@shopify/polaris";
import { css, StyleSheet } from "aphrodite";
import mooshooCheckPhone from "../../assets/care-screener/mooshoo-check-phone.png";
import Button from "../Button";
import Text from "../core/Text";
import Flex from "../core/Flex";
import useCareScreenerWidth from "../../hooks/care-screener/useCareScreenerWidth";
import { logEvent } from "../../actions";
import Spacer from "./Spacer";
import CareScreenerService from "../../services/CareScreenerService";

interface TwoFactorAuthVerificationProps {
  onVerificationComplete: (success: boolean) => void;
  phoneNumber?: string;
  progressComponent?: React.ReactNode;
}

export default function TwoFactorAuthVerification({
  onVerificationComplete,
  progressComponent
}: TwoFactorAuthVerificationProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const smsRequestedRef = useRef(false);
  const challengeToken = sessionStorage.getItem("challengeToken");
  const userId = sessionStorage.getItem("userId");

  const handleSubmit = async () => {
    if (!code.trim()) {
      setError("Please enter the verification code");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await CareScreenerService.verifyTOTP({
        totp: code.trim()
      });

      if (result.success || (result.id && result.token)) {
        logEvent("twoFactorAuthSuccess", { userId });
        onVerificationComplete(true);
      } else {
        setError(result.message || "Invalid verification code");
        logEvent("twoFactorAuthFailed", { userId });
      }
    } catch (error) {
      console.error("2FA verification error:", error);
      setError("Verification failed. Please try again.");
      logEvent("twoFactorAuthError", { userId });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setResending(true);
      setError(null);

      const result = await CareScreenerService.requestSmsVerificationCode();

      if (result.success && result.phone) {
        setPhoneNumber(result.phone);
        logEvent("twoFactorAuthResendCodeSuccess", { userId });
      } else {
        if (result.message && result.message.includes("rate limit")) {
          setError("Too many SMS requests. Please wait a few minutes before trying again.");
        } else {
          setError(result.message || "Failed to resend code");
        }
        logEvent("twoFactorAuthResendCodeFailed", { userId, value: result.message });
      }
    } catch (e) {
      setError("Failed to resend code. Please try again.");
      logEvent("twoFactorAuthResendCodeFailed", { userId, value: e.message });
    } finally {
      setResending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const { widthStyle } = useCareScreenerWidth();

  const maskPhoneNumber = (phone: string) => {
    if (!phone) return "";

    const digits = phone.replace(/\D/g, "");
    if (digits.length >= 10) {
      return `***-***-${digits.slice(-4)}`;
    }
    return phone;
  };

  useEffect(() => {
    const requestInitialSms = async () => {
      if (smsRequestedRef.current) {
        return;
      }
      smsRequestedRef.current = true;

      if (!challengeToken) {
        smsRequestedRef.current = false;
        return;
      }

      try {
        const result = await CareScreenerService.requestSmsVerificationCode();

        if (result.success && result.phone) {
          setPhoneNumber(result.phone);
        } else {
          smsRequestedRef.current = false;
          if (result.message && result.message.includes("rate limit")) {
            setError("Too many SMS requests. Please wait a few minutes before trying again.");
          } else {
            setError(result.message || "Failed to send SMS code");
          }
        }
      } catch (e) {
        smsRequestedRef.current = false;
        setError("Failed to send SMS code. Please try again.");
      }
    };

    requestInitialSms();
  }, [challengeToken]);

  return (
    <Flex className={css(styles.container)} style={widthStyle}>
      {progressComponent}
      <div className={css(styles.columnCentered)}>
        <img src={mooshooCheckPhone} className={css(styles.mooshooCheckPhone)} />
        <Text style={styles.title} theme="manaBlueDark" title size={24}>
          Verify that it's you
        </Text>
        <Text tag="span" style={styles.subtitle} color="#6b7280" size={13}>
          We sent a verification code to {phoneNumber ? maskPhoneNumber(phoneNumber) : "the phone number you gave us"}.
        </Text>
        <Spacer height={20} />

        {error && <div style={{ color: "#d32f2f", fontSize: "14px", marginBottom: "8px" }}>{error}</div>}
        <div className={css(styles.inputWrapper)} onKeyDown={handleKeyDown}>
          <TextField
            placeholder="Verification code"
            onChange={setCode}
            name="code"
            value={code}
            label=""
            autoComplete="one-time-code"
            maxLength={6}
            error={!!error}
          />
        </div>
        <div className={css(styles.buttonWrapper)}>
          <Button
            title="submit"
            disabled={loading || !code.trim()}
            loading={loading}
            onPress={handleSubmit}
            theme="manaBlue"
          />
        </div>
        <div className={css(styles.resendWrapper)}>
          <Text style={styles.resendText} size={13}>
            Didn't receive a code?{" "}
            <span
              className={css(styles.resendLink)}
              onClick={resending ? undefined : handleResendCode}
              style={{ cursor: resending ? "not-allowed" : "pointer" }}
            >
              {resending ? "Sending..." : "Resend"}
            </span>
          </Text>
        </div>
      </div>
    </Flex>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 50,
    marginBottom: 50,
    padding: "5%",
    paddingTop: 10,
    backgroundColor: "#FFFFFF",
    marginLeft: "auto",
    marginRight: "auto",
    borderRadius: 15
  },
  columnCentered: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%"
  },
  image: {
    width: 300,
    height: 150,
    minHeight: 160,
    objectFit: "contain",
    aspectRatio: 1,
    marginTop: 20,
    marginBottom: 10
  },
  title: {
    textAlign: "center",
    marginBottom: 10,
    marginTop: 20
  },
  mooshooCheckPhone: {
    width: 200,
    display: "block",
    marginBottom: 20,
    marginTop: 30
  },
  marginTop: {
    marginTop: 20
  },
  font14: {
    fontSize: 14
  },
  subtitleGray: {
    fontFamily: "Helvetica",
    color: "#636C8C",
    fontSize: 18,
    lineHeight: 1.4,
    fontWeight: 500,
    textAlign: "center"
  },
  forgotPassword: {
    textAlign: "center",
    fontSize: 14,
    lineHeight: 1.4,
    marginTop: 20,
    marginBottom: 10,
    fontWeight: 400
  },
  accentOne: {
    position: "absolute",
    top: -25,
    right: -15,
    zIndex: 50,
    transform: "scaleX(-1) rotate(10deg)"
  },
  accentThree: {
    position: "absolute",
    top: 12,
    left: 10,
    zIndex: 50
  },
  card: {
    background: "#eaf2ff",
    borderRadius: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    padding: 24,
    width: 350,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    position: "relative"
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 16,
    gap: 12
  },
  mascot: {
    width: 56,
    height: 56,
    marginRight: 0
  },
  subtitle: {
    fontWeight: 400,
    lineHeight: 1.3
  },
  inputWrapper: {
    position: "relative",
    marginTop: 12,
    width: "85%",
    maxWidth: 300
  },
  buttonWrapper: {
    width: "85%",
    maxWidth: 300,
    marginTop: 16
  },
  resendWrapper: {
    marginTop: 20,
    textAlign: "center"
  },
  resendText: {
    color: "#6b7280",
    fontSize: 13,
    lineHeight: 1.3
  },
  resendLink: {
    color: "#3b82f6",
    textDecoration: "underline",
    fontWeight: 400
  }
});
